/**
 * Alternative investments in the portal.
 *
 * An alternative investment reports once a quarter; in the months between, its
 * last reported value is carried. Wherever its value is shown, it says which
 * report the value comes from ("לפי דוח 30.09.2026"), so a carried figure is
 * never mistaken for this month's. The report date rides on each month row
 * (`sheet_name` = `alt:<date>`), so nothing extra has to be fetched for it.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { ALT_BENCHMARK_NOTE_HE, altReportDateOf, altReportLabel } from '@agio/core/domain';
import type { PerformanceRow } from '@agio/core';

const AltReportContext = createContext<ReadonlyMap<string, string>>(new Map());

/** The report behind each alternative investment's latest month in `perf`. */
export function altReportsByFund(perf: Pick<PerformanceRow, 'fund_id' | 'date' | 'sheet_name'>[]): Map<string, string> {
  const latest = new Map<string, { date: string; asOf: string }>();
  for (const p of perf) {
    const asOf = altReportDateOf(p.sheet_name);
    if (!asOf) continue;
    const cur = latest.get(p.fund_id);
    if (!cur || p.date > cur.date) latest.set(p.fund_id, { date: p.date, asOf });
  }
  return new Map([...latest].map(([id, v]) => [id, v.asOf]));
}

export function AltReportProvider({ perf, children }: { perf: PerformanceRow[]; children: ReactNode }) {
  const map = useMemo(() => altReportsByFund(perf), [perf]);
  return <AltReportContext.Provider value={map}>{children}</AltReportContext.Provider>;
}

/** The report date behind a fund's shown value — null for anything but an alternative investment. */
export function useAltReport(fundId: string): string | null {
  return useContext(AltReportContext).get(fundId) ?? null;
}

/** "לפי דוח 30.09.2026" next to an alternative investment's name; nothing for any other fund. */
export function AltReportMarker({ fundId }: { fundId: string }) {
  const asOf = useAltReport(fundId);
  if (!asOf) return null;
  return <span className="ms-1.5 whitespace-nowrap text-[0.85em] font-normal text-muted-foreground">({altReportLabel(asOf)})</span>;
}

/** Said wherever the portfolio benchmark is shown for a client holding alternatives. */
export function AltBenchmarkNote() {
  return <p className="text-xs text-muted-foreground">* {ALT_BENCHMARK_NOTE_HE}</p>;
}
